import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bell, Check, CheckCheck, Trash2, ExternalLink, AlertTriangle, AlertCircle, Sparkles } from 'lucide-react';
import { notificacionesAPI } from '../services/api';
import { useConfirm, Tooltip } from './ui';
import Button from './ui/Button';
import Badge from './ui/Badge';

const PAGE_SIZE = 10;

// Icono por tipo (mismo set que el NotificationCenter popup).
const getIcon = (tipo) => {
    if (tipo === 'alerta_sin_comunicacion') return <AlertTriangle size={16} />;
    if (tipo === 'alerta_abandono') return <AlertCircle size={16} />;
    if (tipo.startsWith('sugerencia_ia')) return <Sparkles size={16} />;
    return <Bell size={16} />;
};

const getTipoLabel = (tipo) => ({
    alerta_sin_comunicacion: 'Sin comunicación',
    alerta_abandono: 'Abandono',
    sugerencia_ia_seguimiento: 'Seguimiento IA',
    sugerencia_ia_pendiente: 'Pendiente IA',
    recordatorio_borradores: 'Borradores pendientes'
}[tipo] || tipo);

const formatFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('es-PE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    }).replace(/\./g, '');
};

const NotificacionesPage = () => {
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [filtro, setFiltro] = useState('todas'); // 'todas' | 'no_leidas' | 'leidas'
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [borrandoTodas, setBorrandoTodas] = useState(false);

    // Carga del rango actual. El offset se pasa explicito o se calcula del
    // state actual via setItems(prev) → evita races por offset stale en el
    // closure del useCallback (causaba duplicacion al refrescar tras marcar leida).
    const cargar = useCallback(async (resetear = false) => {
        setLoading(true);
        try {
            // Si reseteamos: offset 0 + reemplazar. Si no: usar el largo actual de items.
            const offsetActual = resetear ? 0 : items.length;
            const res = await notificacionesAPI.getHistorial({
                estado: filtro,
                offset: offsetActual,
                limit: PAGE_SIZE
            });
            const nuevos = res.data || [];
            setItems(prev => {
                if (resetear) return nuevos;
                // Dedup defensivo por id (por si el backend devuelve overlap)
                const idsExistentes = new Set(prev.map(x => x.id));
                return [...prev, ...nuevos.filter(n => !idsExistentes.has(n.id))];
            });
            setTotal(res.pagination?.total || 0);
            setOffset(offsetActual + nuevos.length);
        } catch (err) {
            console.error(err);
            toast.error('Error al cargar el historial');
        } finally {
            setLoading(false);
        }
    }, [filtro, items.length]);

    // Cambio de filtro → reset completo.
    useEffect(() => {
        setOffset(0);
        setItems([]);
        cargar(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtro]);

    const navegar = async (n) => {
        if (!n.leida) {
            try { await notificacionesAPI.marcarLeida(n.id); } catch {}
        }
        if (n.accion_url) navigate(n.accion_url);
    };

    // Borrar inmediato: el undo con timeout causaba que al cambiar de tab,
    // el cargar(true) trajera el item del backend (que aun no se habia borrado)
    // y volviera a aparecer en la lista.
    const eliminar = async (n) => {
        const itemsRespaldo = items;
        // Optimistic update visual
        setItems(prev => prev.filter(x => x.id !== n.id));
        setTotal(t => Math.max(0, t - 1));
        try {
            await notificacionesAPI.eliminar(n.id);
            toast.success('Notificación eliminada');
        } catch (err) {
            toast.error('Error al eliminar');
            setItems(itemsRespaldo);
            setTotal(t => t + 1);
        }
    };

    // Borrar TODAS las leidas (con confirm porque es masivo e irreversible).
    const eliminarTodasLeidas = async () => {
        const ok = await confirm({
            title: 'Eliminar todas las leídas',
            message: 'Esta acción no se puede deshacer. Las notificaciones no leídas se conservarán.',
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        setBorrandoTodas(true);
        try {
            const res = await notificacionesAPI.eliminarLeidas();
            toast.success(`${res.eliminadas || 0} notificaciones eliminadas`);
            setOffset(0);
            setItems([]);
            await cargar(true);
        } catch (err) {
            toast.error('Error al eliminar');
        } finally {
            setBorrandoTodas(false);
        }
    };

    const hayMas = items.length < total;
    const cantidadLeidasMostradas = items.filter(n => n.leida).length;

    return (
        <div className="card">
            <div className="card-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-col">
                    <h3 style={{ margin: 0 }}>Historial de notificaciones</h3>
                    <span className="text-[11px] text-muted mt-0.5">
                        {total} {total === 1 ? 'notificación' : 'notificaciones'} en {filtro === 'todas' ? 'total' : filtro.replace('_', ' ')}
                    </span>
                </div>
                <Button
                    variant="soft"
                    size="sm"
                    leftIcon={<Trash2 size={14} />}
                    onClick={eliminarTodasLeidas}
                    disabled={borrandoTodas || cantidadLeidasMostradas === 0}
                >
                    Eliminar leídas
                </Button>
            </div>

            <div className="card-body">
                {/* Tabs de filtro */}
                <div className="notif-page-tabs">
                    {['todas', 'no_leidas', 'leidas'].map(f => (
                        <button
                            key={f}
                            className={`notif-page-tab ${filtro === f ? 'active' : ''}`}
                            onClick={() => setFiltro(f)}
                        >
                            {f === 'todas' ? 'Todas' : f === 'no_leidas' ? 'No leídas' : 'Leídas'}
                        </button>
                    ))}
                </div>

                {/* Lista */}
                {loading && items.length === 0 ? (
                    <div className="notif-empty">
                        <Bell size={32} style={{ opacity: 0.3 }} />
                        <p>Cargando…</p>
                    </div>
                ) : items.length === 0 ? (
                    <div className="notif-empty">
                        <Bell size={32} style={{ opacity: 0.3 }} />
                        <p>
                            {filtro === 'no_leidas' ? 'No tienes notificaciones pendientes' :
                                filtro === 'leidas' ? 'No tienes notificaciones leídas' :
                                    'Aún no tienes notificaciones'}
                        </p>
                    </div>
                ) : (
                    <div className="notif-page-list">
                        {items.map(n => (
                            <div key={n.id} className={`notif-page-item ${!n.leida ? 'unread' : 'read'}`}>
                                <div className="notif-page-item-icon">{getIcon(n.tipo)}</div>
                                <div className="notif-page-item-body">
                                    <div className="notif-page-item-head">
                                        <Badge color="neutral" size="sm">{getTipoLabel(n.tipo)}</Badge>
                                        <span className="notif-page-item-titulo">{n.titulo}</span>
                                    </div>
                                    {n.mensaje && <div className="notif-page-item-mensaje">{n.mensaje}</div>}
                                    {/* Fecha al pie, sutil, no compite con el titulo */}
                                    <div className="notif-page-item-time">{formatFecha(n.created_at)}</div>
                                </div>
                                {/* Acciones a la derecha. Orden: leida -> ver -> eliminar.
                                    Si esta leida, mostramos un indicador silencioso (doble check)
                                    en lugar del boton; si no, el boton "Marcar leida" en verde. */}
                                <div className="notif-page-item-actions">
                                    {!n.leida ? (
                                        <button
                                            className="notif-action version"
                                            onClick={async () => {
                                                // Optimistic local: marca leida sin refetch (evita race + duplicacion)
                                                try {
                                                    await notificacionesAPI.marcarLeida(n.id);
                                                    setItems(prev => prev.map(x => x.id === n.id ? { ...x, leida: true, leida_at: new Date().toISOString() } : x));
                                                } catch {
                                                    toast.error('Error al marcar como leída');
                                                }
                                            }}
                                        >
                                            <Check size={14} />
                                            <span>Marcar leída</span>
                                        </button>
                                    ) : (
                                        <span className="notif-action-status leida">
                                            <CheckCheck size={14} />
                                            <span>Leída</span>
                                        </span>
                                    )}
                                    {n.accion_url && (
                                        <button
                                            className="notif-action detalle"
                                            onClick={() => navegar(n)}
                                        >
                                            <ExternalLink size={14} />
                                            <span>{n.cotizacion_id ? 'Ver cotización' : 'Abrir'}</span>
                                        </button>
                                    )}
                                    <Tooltip content="Eliminar notificación">
                                        <button
                                            className="notif-action delete"
                                            onClick={() => eliminar(n)}
                                            aria-label="Eliminar notificación"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}

                        {hayMas && (
                            <div className="notif-page-load-more">
                                <Button variant="soft" size="sm" onClick={() => cargar(false)} disabled={loading}>
                                    {loading ? 'Cargando…' : `Cargar más (${total - items.length} restantes)`}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificacionesPage;
